import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { WebService } from '../web-service';
import { catchError, Observable, of } from 'rxjs';
import {
  AuthSession,
  confirmResetPassword,
  confirmSignUp,
  ConfirmSignUpOutput,
  fetchAuthSession,
  resendSignUpCode,
  ResendSignUpCodeOutput,
  resetPassword,
  ResetPasswordOutput,
  signIn,
  SignInOutput,
  signOut,
  signUp,
  SignUpOutput,
  updatePassword,
} from '@aws-amplify/auth';
import { CheckAvailabilityResponse, DeleteAccountResponse } from './user-auth-service-types';
import { TabsState } from 'app/state-management/tabs-state';

export interface LoginRequest {
  email: string;
  passwordHash: string;
  rememberMe: boolean;
}

export interface RegisterRequest {
  username: string;
  email: string;
  passwordHash: string;
  rememberMe: boolean;
  data?: UserData;
}

export interface UserData {
  'username'?: string;
  'custom:firstName'?: string;
  'custom:lastName'?: string;
}

export interface ChangePasswordRequest {
  passwordHash: string;
  key: string;
}

@Injectable({
  providedIn: 'root',
})
export class UserAuthService {
  constructor(
    private http: HttpClient,
    private webService: WebService,
    private tabsState: TabsState,
  ) {}

  register(request: RegisterRequest): Promise<SignUpOutput> {
    return new Promise(async (resolve, reject) => {
      try {
        const res = await signUp({
          username: request.username,
          password: request.passwordHash,
          options: {
            userAttributes: {
              email: request.email,
              ...request.data,
            },
          },
        });
        resolve(res);
      } catch (error) {
        reject(error);
      }
    });
  }

  login(username: string, password: string): Promise<SignInOutput> {
    return new Promise(async (resolve, reject) => {
      try {
        const res = await signIn({ username, password });
        resolve(res);
      } catch (error) {
        reject(error);
      }
    });
  }

  logout(): Promise<void> {
    return new Promise(async (resolve, reject) => {
      try {
        const res = await signOut();
        resolve(res);
      } catch (error) {
        reject(error);
      }
    });
  }

  globalLogout(): Promise<void> {
    return new Promise(async (resolve, reject) => {
      try {
        const res = await signOut({ global: true });
        resolve(res);
      } catch (error) {
        reject(error);
      }
    });
  }

  hasRole(role: string): Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      try {
        fetchAuthSession().then((res) => {
          if (res.tokens?.idToken?.payload['cognito:groups']) {
            resolve((res.tokens?.idToken?.payload['cognito:groups'] as string[]).includes(role));
          } else {
            resolve(false);
          }
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  getSession(): Promise<AuthSession> {
    return new Promise(async (resolve, reject) => {
      try {
        await fetchAuthSession()
          .then((res: AuthSession) => {
            if (res.tokens) {
              resolve(res);
            } else {
              resolve(res);
            }
          })
          .catch((err: any) => {
            resolve({});
          });
      } catch (error) {
        reject(error);
      }
    });
  }

  verify(username: string, token: string): Promise<ConfirmSignUpOutput> {
    return new Promise(async (resolve, reject) => {
      try {
        const res = await confirmSignUp({ username, confirmationCode: token });
        resolve(res);
      } catch (error) {
        reject(error);
      }
    });
  }

  requestPasswordReset(email: string): Promise<ResetPasswordOutput> {
    return new Promise(async (resolve, reject) => {
      try {
        const res = await resetPassword({ username: email });
        resolve(res);
      } catch (error) {
        reject(error);
      }
    });
  }

  changePassword(email: string, code: string, newPassword: string): Promise<void> {
    return new Promise(async (resolve, reject) => {
      try {
        const res = await confirmResetPassword({
          username: email,
          confirmationCode: code.toString(),
          newPassword,
        });
        resolve(res);
      } catch (error) {
        reject(error);
      }
    });
  }

  changePasswordAuthenticated(oldPassword: string, newPassword: string): Promise<void> {
    return new Promise(async (resolve, reject) => {
      try {
        const res = await updatePassword({ oldPassword, newPassword });

        resolve(res);
      } catch (error) {
        reject(error);
      }
    });
  }

  checkAvailability(username: string): Observable<CheckAvailabilityResponse> {
    return this.http.get<CheckAvailabilityResponse>(
      encodeURI(`${this.webService.rootService}/username?username=${username}`),
    );
  }

  deleteAccount(): Promise<DeleteAccountResponse> {
    return new Promise(async (resolve, reject) => {
      try {
        this.http
          .delete<DeleteAccountResponse>(`${this.webService.rootService}/user`)
          .pipe(
            catchError((err: unknown) => {
              reject(err);
              return of(undefined);
            }),
          )
          .subscribe((res: DeleteAccountResponse | undefined) => {
            if (res) {
              resolve(res);
            }
          });
      } catch (error) {
        reject(error);
      }
    });
  }

  requestVerificationCode(username: string): Promise<ResendSignUpCodeOutput> {
    return new Promise(async (resolve, reject) => {
      try {
        const res = await resendSignUpCode({ username });
        resolve(res);
      } catch (error) {
        reject(error);
      }
    });
  }
}
